import { message, Modal, Select } from "antd";
import svg from "../../../../Assets/svg";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  ButtonWithLoader,
  PageLoader,
} from "../../../../Components/Components";
import { useDispatch } from "react-redux";
import {
  AddQuestionAPI,
  EditQuestionAPI,
  EditQuizAPI,
  GetQuestionsAPI,
  GetQuestionTypesAPI,
} from "../../../Redux/quiz";
import { AnswerTypes } from "../QuestionBank";

const AddQuestionFormModal = ({
  isModalOpen,
  onClose,
  questionId,
  quizId,
  questionsLength,
  onDuplicate,
}) => {
  const dispatch = useDispatch();
  const [title, setTitle] = useState("");
  const [answerType, setAnswerType] = useState(AnswerTypes.MultipleChoice);

  const [reflection, setReflection] = useState("");
  const [reference, setReference] = useState("");

  const [selectedQuestionType, setSelectedQuestionType] = useState([]);

  const [fetching, setFetching] = useState(questionId ? true : false);

  const [optionsList, setOptionsList] = useState([
    { id: uuidv4() },
    { id: uuidv4() },
  ]);

  const [questionTypeOptions, setQuestionTypeOptions] = useState([]);

  const optionDataRef = useRef(new Map());

  const [, forceUpdate] = useState({});

  const [isSaving, setIsSaving] = useState(false);

  const [savingAndAdding, setSavingAndAdding] = useState(false);

  // Initialize option data when IDs are created
  useEffect(() => {
    optionsList.forEach(({ id }) => {
      if (!optionDataRef.current.has(id)) {
        optionDataRef.current.set(id, { label: "", isCorrect: false });
      }
    });
  }, [optionsList]);

  const fetchQuestions = async () => {
    setFetching(true);
    const result = await dispatch(GetQuestionsAPI({ id: questionId }));
    if (result?.success) {
      const questionData = result.data.data[0];
      if (questionData) {
        setTitle(questionData.question);
        setAnswerType(
          questionData.ans_type === "multiple_choice"
            ? AnswerTypes.MultipleChoice
            : AnswerTypes.TextArea
        );
        setSelectedQuestionType(questionData.tags);
        if (questionData.ans_type === "multiple_choice") {
          setReflection(questionData.reflection);
          setReference(questionData.ay_reflection);

          const formattedOptions = questionData.options.map((opt) => ({
            id: opt._id,
          }));
          setOptionsList(formattedOptions);

          const newMap = new Map();
          questionData.options.forEach((opt) => {
            newMap.set(opt._id, {
              label: opt.text,
              isCorrect: opt.is_correct,
              backendId: opt._id,
            });
          });
          optionDataRef.current = newMap;
        }
      }
    }
    setFetching(false);
  };

  useEffect(() => {
    if (questionId) {
      fetchQuestions();
    }
  }, [questionId]);

  const fetchQuestionTypes = async () => {
    // setFetching(true);
    const result = await dispatch(GetQuestionTypesAPI());
    if (result?.success) {
      setQuestionTypeOptions(result.data.map((i) => ({ value: i, label: i })));
    }
    // setFetching(false);
  };

  useEffect(() => {
    fetchQuestionTypes();
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleMarkCorrect = (selectedId) => {
    const newMap = new Map();
    for (const [id, val] of optionDataRef.current.entries()) {
      newMap.set(id, {
        ...val,
        isCorrect: id === selectedId,
      });
    }
    optionDataRef.current = newMap;
    forceUpdate({});
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = optionsList.findIndex((i) => i.id === active.id);
      const newIndex = optionsList.findIndex((i) => i.id === over?.id);
      const newList = arrayMove(optionsList, oldIndex, newIndex);
      setOptionsList(newList);

      const newMap = new Map();
      for (const { id } of newList) {
        const prevData = optionDataRef.current.get(id);
        newMap.set(id, prevData || { label: "", isCorrect: false });
      }
      optionDataRef.current = newMap;
    }
  };

  const SortableOptionRow = ({ optionId }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: optionId });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    const data = optionDataRef.current.get(optionId) || {};
    const [localLabel, setLocalLabel] = useState(data.label);

    useEffect(() => {
      setLocalLabel(data.label);
    }, [data.label]);

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`row align-items-center gy-2 mb-md-2 mb-3 ${
          isDragging ? "dragging" : ""
        }`}
      >
        <div className="col-lg-8 col-md-7">
          <input
            // maxLength={100}
            placeholder="Enter Option"
            className="form-control form-control-lg"
            value={localLabel}
            onChange={(e) => {
              const updatedLabel = e.target.value;
              optionDataRef.current.set(optionId, {
                ...data,
                label: updatedLabel,
              });
              setLocalLabel(updatedLabel);
            }}
          />
        </div>
        <div className="col-lg-3 col-md-3 col-9 text-nowrap">
          <div className="d-flex align-items-center gap-2">
            <input
              type="checkbox"
              className="custom-round-checkbox"
              checked={data.isCorrect}
              onChange={() => handleMarkCorrect(optionId)}
            />
            <label className={`${data.isCorrect ? "text-green" : ""}`}>
              {data.isCorrect ? "Correct Answer" : "Mark As Correct Answer"}
            </label>
          </div>
        </div>
        <div className="col-lg-1 col-md-2 col-3">
          <div className="d-flex gap-2 align-items-center justify-content-end">
            <span
              className={`d-inline-flex svg-24 ${
                isDragging ? "cursor-grabbing" : "cursor-grab"
              }`}
              {...attributes}
              {...listeners}
            >
              {svg.dragHandle}
            </span>
            <span
              className="d-inline-flex svg-24"
              onClick={() => {
                setOptionsList((prev) => prev.filter((o) => o.id !== optionId));
                optionDataRef.current.delete(optionId);
              }}
            >
              {svg.deleteBanner}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const handleSave = (addAndSaveNew = false) => {
    const finalOptions = Array.from(optionDataRef.current.entries()).map(
      ([id, value]) => ({
        id,
        ...value,
      })
    );

    if (!title.trim()) {
      message.error("Title is required!");
      return;
    }

    if (answerType === AnswerTypes.MultipleChoice) {
      if (finalOptions.length < 2) {
        message.error("At least two options are required!");
        return;
      }

      const emptyOption = finalOptions.find((o) => !o.label.trim());
      if (emptyOption) {
        message.error("Please fill in all option fields.");
        return;
      }

      const correctOptionsCount = finalOptions.filter(
        (o) => o.isCorrect
      ).length;
      if (correctOptionsCount !== 1) {
        message.error("Exactly one option must be marked as correct!");
        return;
      }
      if (!reflection.trim()) {
        message.error("Reflection is required!");
        return;
      }

      if (!reference.trim()) {
        message.error("Reference is required!");
        return;
      }
    }

    if (selectedQuestionType.length === 0) {
      message.error("At least one question type must be selected!");
      return;
    }

    if (quizId) {
      const dataToSend = {
        ...(questionId ? { id: questionId } : {}),
        quiz_id: quizId,
        type: "new",
        questions: [
          {
            question: title,
            ans_type:
              answerType === AnswerTypes.MultipleChoice
                ? "multiple_choice"
                : "text",
            ...(answerType === AnswerTypes.MultipleChoice
              ? {
                  options: finalOptions.map((o) => {
                    const optionPayload = {
                      text: o.label,
                      is_correct: o.isCorrect,
                    };
                    if (o.backendId) {
                      optionPayload._id = o.backendId;
                    }
                    return optionPayload;
                  }),

                  reflection: reflection,
                  ay_reflection: reference,
                }
              : {}),
            tags: selectedQuestionType,
          },
        ],
      };

      if (addAndSaveNew) {
        setSavingAndAdding(true);
      } else {
        setIsSaving(true);
      }

      dispatch(
        EditQuizAPI(
          dataToSend,
          () => {
            if (addAndSaveNew) {
              setSavingAndAdding(false);
              onClose(true, true);
              fetchQuestionTypes();
              setTitle("");
              setAnswerType(AnswerTypes.MultipleChoice);
              setSelectedQuestionType([]);
              setReflection("");
              setReference("");
              setOptionsList([{ id: uuidv4() }, { id: uuidv4() }]);
              optionDataRef.current.clear();
            } else {
              setIsSaving(false);
              onClose(true);
            }
          },
          () => {
            if (addAndSaveNew) {
              setSavingAndAdding(false);
            } else {
              setIsSaving(false);
            }
          },
          (id) => {
            if (onDuplicate) {
              message.error(
                "Question already exists! Fetching duplicate question..."
              );
              onDuplicate(id);
            }
          }
        )
      );
    } else {
      const dataToSend = {
        ...(questionId ? { id: questionId } : {}),
        question: title,
        ans_type:
          answerType === AnswerTypes.MultipleChoice
            ? "multiple_choice"
            : "text",
        ...(answerType === AnswerTypes.MultipleChoice
          ? {
              options: finalOptions.map((o) => {
                const optionPayload = {
                  text: o.label,
                  is_correct: o.isCorrect,
                };
                if (o.backendId) {
                  optionPayload._id = o.backendId;
                }
                return optionPayload;
              }),

              reflection: reflection,
              ay_reflection: reference,
            }
          : {}),
        tags: selectedQuestionType,
      };

      setIsSaving(true);
      if (questionId) {
        dispatch(
          EditQuestionAPI(
            dataToSend,
            () => {
              setIsSaving(false);
              onClose(true);
            },
            () => {
              setIsSaving(false);
            }
          )
        );
      } else {
        dispatch(
          AddQuestionAPI(
            dataToSend,
            () => {
              setIsSaving(false);
              onClose(true);
            },
            () => {
              setIsSaving(false);
            }
          )
        );
      }
    }

    // setIsSaving(true);
  };

  return (
    <Modal
      title={
        <div className="modal-header border-bottom pb-2 fw-bold fs-4 ff">
          {quizId
            ? `Question-${questionsLength}`
            : questionId
            ? "Edit Question"
            : "Add Question"}
        </div>
      }
      open={isModalOpen}
      onCancel={() => onClose()}
      footer={
        fetching ? null : quizId ? (
          <div className="d-flex justify-content-center align-items-center gap-3">
            <ButtonWithLoader
              className="btn btn-secondary btn-lg btn-50 min-w-174 ku-btn rounded-pill px-4  text-white bg-orange ff border-0"
              onClick={() => handleSave(false)}
              isLoading={isSaving}
              disabled={savingAndAdding}
            >
              Save
            </ButtonWithLoader>
            <ButtonWithLoader
              className="btn btn-primary btn-lg ku-btn px-4 ff"
              onClick={() => handleSave(true)}
              isLoading={savingAndAdding}
              disabled={isSaving}
            >
              Save & Add New
            </ButtonWithLoader>
          </div>
        ) : (
          <div className="d-flex justify-content-center align-items-center">
            <ButtonWithLoader
              className="btn btn-primary btn-lg ku-btn btn-50 min-w-174 px-4 ff"
              onClick={() => handleSave(false)}
              isLoading={isSaving}
            >
              Save
            </ButtonWithLoader>
          </div>
        )
      }
      centered
      closeIcon={svg?.modalClose}
      className="custom-modal"
      width={"67.5rem"}
      maskClosable={false}
    >
      {fetching ? (
        <div className="d-flex justify-content-center align-items-center px-5 py-5">
          <PageLoader />
        </div>
      ) : (
        <div>
          {/* Title */}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <label className="form-label mb-1 fw-bold ff">Title</label>
              <span className="text-muted lh-0 fs-12">
                {title ? title?.length : 0}/100
              </span>
            </div>
            <input
              maxLength={100}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter Title"
              className="form-control form-control-lg"
            />
          </div>

          {/* Answer Type */}
          <div className="mb-3">
            <label className="form-label mb-2 fw-bold ff">Answer Type</label>
            <div className="d-flex flex-wrap gap-3">
              {Object.entries(AnswerTypes).map(([key, label]) => (
                <div className="form-check form-custom-radio" key={key}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="answerType"
                    id={label}
                    value={label}
                    checked={answerType === label}
                    onChange={() => {
                      setAnswerType(label);
                      if (label === AnswerTypes.TextArea) {
                        setOptionsList([{ id: uuidv4() }, { id: uuidv4() }]);
                        optionDataRef.current = new Map();
                        setReference("");
                        setReflection("");
                      }
                    }}
                  />
                  <label className="form-check-label fs-6 ff" htmlFor={label}>
                    {label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Options */}
          {answerType === AnswerTypes.MultipleChoice && (
            <>
              <div className="mb-3">
                <div className="">
                  <div className="row gy-3">
                    <div className="col-lg-8 col-md-7">
                      <div className="d-flex align-items-center justify-content-between">
                        <label className="form-label mb-0 fw-bold ff">
                          Options
                        </label>
                        <div className="text-muted lh-0 fs-12 ff">
                          Minimum 2 option required
                        </div>
                      </div>
                    </div>
                  </div>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                    modifiers={[
                      restrictToParentElement,
                      restrictToVerticalAxis,
                    ]}
                  >
                    <SortableContext
                      items={optionsList.map((opt) => opt.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {optionsList.map((opt) => (
                        <SortableOptionRow key={opt.id} optionId={opt.id} />
                      ))}
                    </SortableContext>
                  </DndContext>
                </div>
                <div
                  className="text-orange fw-bold ff mt-2 d-inline"
                  role="button"
                  onClick={() => {
                    const newId = uuidv4();
                    optionDataRef.current.set(newId, {
                      label: "",
                      isCorrect: false,
                    });
                    setOptionsList((prev) => [...prev, { id: newId }]);
                  }}
                >
                  + Add Option
                </div>
              </div>
              <div className="mb-3">
                <div className="row gy-3">
                  <div className="col-md-6">
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <label className="form-label mb-0 fw-bold ff">
                          Reflection
                        </label>
                        <span className="text-muted lh-0 fs-12">
                          {reflection ? reflection?.length : 0}/200
                        </span>
                      </div>
                      <textarea
                        maxLength={200}
                        rows={3}
                        value={reflection}
                        onChange={(e) => setReflection(e.target.value)}
                        placeholder="Enter Reflection"
                        className="form-control form-control-lg"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <label className="form-label mb-0 fw-bold ff">
                          Reference
                        </label>
                        <span className="text-muted lh-0 fs-12">
                          {reference ? reference?.length : 0}/200
                        </span>
                      </div>
                      <textarea
                        maxLength={200}
                        rows={3}
                        value={reference}
                        onChange={(e) => setReference(e.target.value)}
                        placeholder="Enter Reference"
                        className="form-control form-control-lg"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Question Type */}
          <div>
            <label className="form-label mb-2 fw-bold ff">Question Type</label>
            <Select
              // mode="multiple"
              mode="tags"
              tokenSeparators={[","]}
              allowClear
              showSearch
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
              placeholder="Select Question Type"
              suffixIcon={null}
              options={questionTypeOptions}
              value={selectedQuestionType}
              onChange={(value) => setSelectedQuestionType(value)}
              className="w-100 customSelect customMultipleSelect"
            />
          </div>
        </div>
      )}
    </Modal>
  );
};

export default AddQuestionFormModal;
